import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Switch,
  TextField,
  Typography,
  FormHelperText,
  Button,
} from '@mui/material';
import { BackButton } from '../../components/Buttons/BackButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { HOMESCREEN_BANNER_ROUTE } from '../../constants/routes';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useState } from 'react';
import { HomebannerService } from '../../api/services/homebannerService';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import { ICreateEditHomebannerPayload } from '../../types/api/request/homebannersrequest';
import { ICreateEditHomescreenBannerLocationState } from '../../types/homebanners';

export const CreateEditHomescreenBannerPage = () => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<string | null>(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const { selectedBannerGroup, banner } =
    location.state as ICreateEditHomescreenBannerLocationState;
  const label = banner ? 'Edit Banner' : 'Create Banner';

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ICreateEditHomebannerPayload>({
    defaultValues: {
      name: banner ? banner.name : '',
      sequence: banner ? banner.sequence : 0,
      is_active: banner ? banner.is_active : false,
      group: banner ? banner.group : selectedBannerGroup || '',
      image: banner ? banner.image_url : null,
      data_list: banner ? banner.data_list : [],
      id: banner ? banner.id : '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'data_list',
  });

  const onSubmit = async (data: ICreateEditHomebannerPayload) => {
    try {
      const cleanedDataList =
        data.data_list?.filter((item) => item.field_name?.trim() || item.field_value?.trim()) || [];

      data.data_list = cleanedDataList;

      if (banner) {
        if (typeof data.image === 'string') {
          data.image = null;
        }

        await HomebannerService.editHomebanner(data);
        dispatch(
          showNotification({
            message: 'Successfully edited homebanner.',
          }),
        );
      } else {
        await HomebannerService.createHomebanner(data);
        dispatch(
          showNotification({
            message: 'Successfully created homebanner.',
          }),
        );
      }

      navigate(HOMESCREEN_BANNER_ROUTE);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: `Failed to ${label}. ` + error?.response?.data?.error || '',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      <Box mb={2}>
        <BackButton onClick={() => navigate(HOMESCREEN_BANNER_ROUTE)} />
        <span style={{ fontSize: 12 }}>Back to Homebanners</span>
      </Box>

      <Paper elevation={1} sx={{ p: 3, maxWidth: 900 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          {label}
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box>
            {/* Active Switch */}
            <FormControl fullWidth>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label="Active"
                  />
                )}
              />
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 1fr',
              },
              columnGap: 3,
              rowGap: 2.5,
            }}
          >
            {/* Name */}
            <FormControl fullWidth error={!!errors.name}>
              <FormLabel>Name</FormLabel>
              <TextField
                size="small"
                placeholder="Enter banner name"
                {...register('name', { required: 'Name is required' })}
                error={!!errors.name}
              />
              {errors.name && <FormHelperText>{errors.name.message}</FormHelperText>}
            </FormControl>

            {/* Sequence */}
            <FormControl fullWidth error={!!errors.sequence}>
              <FormLabel>Sequence</FormLabel>
              <TextField
                size="small"
                type="number"
                placeholder="Enter sequence"
                {...register('sequence', {
                  required: 'Sequence is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Must be 0 or greater' },
                })}
                error={!!errors.sequence}
              />
              {errors.sequence && <FormHelperText>{errors.sequence.message}</FormHelperText>}
            </FormControl>

            {/* Group (Disabled for now) */}
            <FormControl fullWidth>
              <FormLabel>Group</FormLabel>
              <TextField size="small" placeholder="Banner group" disabled {...register('group')} />
            </FormControl>

            {/* Dynamic Data List Section */}
            <Box sx={{ gridColumn: '1 / -1', mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Additional Data Fields
              </Typography>

              {fields.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
                    gap: 2,
                    mb: 2,
                    alignItems: 'center',
                  }}
                >
                  {/* Field Name */}
                  <TextField
                    size="small"
                    label="Field Name"
                    {...register(`data_list.${index}.field_name` as const, {
                      required: 'Field name is required',
                    })}
                    error={!!errors.data_list?.[index]?.field_name}
                    helperText={errors.data_list?.[index]?.field_name?.message}
                  />

                  {/* Field Value */}
                  <TextField
                    size="small"
                    label="Field Value"
                    {...register(`data_list.${index}.field_value` as const, {
                      required: 'Field value is required',
                    })}
                    error={!!errors.data_list?.[index]?.field_value}
                    helperText={errors.data_list?.[index]?.field_value?.message}
                  />

                  {/* Remove Button */}
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => remove(index)}
                    disabled={fields.some((x) => !x.field_name?.length)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}

              {/* Add Row Button */}
              <Button
                variant="contained"
                onClick={() => append({ field_name: '', field_value: '' })}
              >
                Add Row
              </Button>
            </Box>

            {/* Image Upload */}
            <FormControl fullWidth error={!!errors.image}>
              <FormLabel>Banner Image</FormLabel>

              <Controller
                name="image"
                control={control}
                rules={{ required: 'Banner image is required' }}
                render={({ field }) => (
                  <>
                    <Button variant="outlined" component="label" size="small">
                      Upload Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          field.onChange(file);

                          if (file) {
                            setPreview(URL.createObjectURL(file));
                          } else {
                            setPreview(null);
                          }
                        }}
                      />
                    </Button>

                    {field.value && (
                      <Typography variant="body2" mt={1}>
                        Selected:{' '}
                        {typeof field.value === 'string' ? field.value : field.value?.name || ''}
                      </Typography>
                    )}

                    {preview && (
                      <Box mt={2}>
                        <img
                          src={preview}
                          alt="Preview"
                          style={{
                            width: '100%',
                            maxHeight: 200,
                            objectFit: 'contain',
                            borderRadius: 8,
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              />

              {errors.image && <FormHelperText>{errors.image.message}</FormHelperText>}
            </FormControl>
          </Box>

          {/* Submit Button Full Width */}
          <Box
            sx={{
              justifySelf: 'end', // pushes to right of its grid cell
              mt: 2,
            }}
          >
            <Button type="submit" variant="contained" disabled={isSubmitting} fullWidth>
              {isSubmitting ? 'Loading...' : label}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};
